import React from "react";

/**
 * Reusable circular score ring.
 *   <ScoreRing score={82} size={42} />
 *
 * Color band:
 *   ≥85 green, ≥70 lime, ≥55 amber, ≥40 orange, else red.
 * Renders "—" when score is null/undefined; pulls its own colour for the
 * trailing ring track from currentColor so it inherits in dark mode.
 */
const bandColor = (s) => {
  if (s == null || isNaN(s)) return "#94A3B8";
  if (s >= 85) return "#10B981";
  if (s >= 70) return "#22C55E";
  if (s >= 55) return "#F59E0B";
  if (s >= 40) return "#F97316";
  return "#EF4444";
};

const ScoreRing = ({
  score,
  size = 42,
  thickness,
  showLabel = true,
  trackColor = "rgba(148,163,184,0.18)",
  labelColor = "#0F172A",
  className,
  title,
}) => {
  const stroke = thickness ?? Math.max(3, Math.round(size * 0.1));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const safe = score == null || isNaN(score) ? null : Math.max(0, Math.min(100, Number(score)));
  const offset = safe == null ? circumference : circumference - (safe / 100) * circumference;
  const color = bandColor(safe);
  const fontSize = Math.max(9, Math.round(size * 0.32));

  return (
    <div
      className={className}
      title={title ?? (safe == null ? "No score" : `${Math.round(safe)}/100`)}
      style={{ position: "relative", width: size, height: size, display: "inline-block", flexShrink: 0 }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.6s ease, stroke 0.3s ease" }}
        />
      </svg>
      {showLabel && (
        <div
          style={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize, fontWeight: 700, color: labelColor, lineHeight: 1,
          }}
        >
          {safe == null ? "—" : Math.round(safe)}
        </div>
      )}
    </div>
  );
};

export default ScoreRing;
